import { useState } from 'react';
import { CheckCircle2, AlertCircle, Loader2, Trash2 } from 'lucide-react';
import { apiClient } from '../../api/client';
import { setSellerSigState, type SellerSigState } from '../../utils/sellerSignature';
import SignaturePad from './SignaturePad';

interface Props {
  scope: 'company' | 'mine';
  current: { has: boolean; signerName: string; dataUrl: string | null };
  defaultName: string;
  nameLabel: string;
  namePlaceholder: string;
}

function errText(e: unknown): string {
  const r = (e as { response?: { data?: { message?: string } } })?.response?.data;
  return r?.message || 'Něco se nepovedlo. Zkuste to prosím znovu.';
}

/** Nastavení jednoho podpisu prodejce (za bazar, nebo můj vlastní): nakreslit / nahrát, jméno, uložit, smazat. */
export default function SellerSignatureEditor({ scope, current, defaultName, nameLabel, namePlaceholder }: Props) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [name, setName] = useState(current.signerName || defaultName);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [padKey, setPadKey] = useState(0);

  const base = `/signatures/${scope}`;

  async function save() {
    if (!dataUrl) return;
    setBusy(true); setMsg(null);
    try {
      const r = await apiClient.put(base, { dataUrl, signerName: name });
      setSellerSigState(r.data.data as SellerSigState);
      setDataUrl(null); setPadKey(k => k + 1);
      setMsg({ ok: true, text: 'Podpis je uložený.' });
    } catch (e) { setMsg({ ok: false, text: errText(e) }); }
    setBusy(false);
  }

  async function remove() {
    if (!confirm('Opravdu smazat uložený podpis? Nové smlouvy se pak budou tisknout bez něj.')) return;
    setBusy(true); setMsg(null);
    try {
      const r = await apiClient.delete(base);
      setSellerSigState(r.data.data as SellerSigState);
      setMsg({ ok: true, text: 'Podpis je smazaný.' });
    } catch (e) { setMsg({ ok: false, text: errText(e) }); }
    setBusy(false);
  }

  return (
    <div className="space-y-4">
      {current.has && current.dataUrl && (
        <div className="flex items-center gap-4 p-3 rounded-xl border border-green-200 bg-green-50">
          <img src={current.dataUrl} alt="Uložený podpis" className="h-16 bg-white border border-gray-200 rounded-lg p-1" />
          <div className="flex-1 text-sm">
            <div className="font-medium text-gray-900 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-green-600" /> Podpis je uložený</div>
            <div className="text-xs text-gray-500">{current.signerName || '—'}</div>
          </div>
          <button onClick={remove} disabled={busy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50">
            <Trash2 className="h-3.5 w-3.5" /> Smazat
          </button>
        </div>
      )}

      <div>
        <div className="text-sm font-medium text-gray-700 mb-1.5">{current.has ? 'Nahradit novým podpisem' : 'Nakreslete podpis'}</div>
        <SignaturePad key={padKey} onChange={setDataUrl} disabled={busy} />
      </div>

      <div>
        <label className="text-sm font-medium text-gray-700 block mb-1.5">{nameLabel}</label>
        <input value={name} onChange={e => setName(e.target.value)} maxLength={200} placeholder={namePlaceholder}
               className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <p className="text-xs text-gray-400 mt-1">Zobrazí se u podpisu v podepsaném dokumentu a v záznamu o podpisu.</p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={save} disabled={busy || !dataUrl || name.trim().length < 2}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Uložit podpis
        </button>
        {msg && (
          <span className={`text-sm flex items-center gap-1.5 ${msg.ok ? 'text-green-700' : 'text-red-600'}`}>
            {msg.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />} {msg.text}
          </span>
        )}
      </div>
    </div>
  );
}
